import React, { useState, useRef, lazy } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, Compass, Brain, Target, Puzzle, Lightbulb, Trophy } from 'lucide-react';
import { motion } from 'framer-motion';
const includes = [{
  id: 1,
  icon: Compass,
  title: 'LITTLE EXPLORERS',
  subtitle: 'Ages 3-4',
  color: 'bg-[#008B8B]',
  image: 'https://cdn.magicpatterns.com/uploads/vZ4ZqQR6WxhyPHh7sGrfDW/PORT_1_NEURO.png'
}, {
  id: 2,
  icon: Brain,
  title: 'BRAIN BUILDERS',
  subtitle: 'Ages 5-6',
  color: 'bg-[#6B8E23]',
  image: 'https://cdn.magicpatterns.com/uploads/gv9oZcEF7yupGNHTGhJpq6/PORT_2_NEURO.png'
}, {
  id: 3,
  icon: Target,
  title: 'FOCUS WARRIORS',
  subtitle: 'Ages 7-8',
  color: 'bg-[#4169E1]',
  image: 'https://cdn.magicpatterns.com/uploads/ihbUkPZh3bYk5VEJNrDpvS/PORT_3_NEURO.png'
}, {
  id: 4,
  icon: Puzzle,
  title: 'LOGIC MASTERS',
  subtitle: 'Ages 9-10',
  color: 'bg-[#8A2BE2]',
  image: 'https://cdn.magicpatterns.com/uploads/mPTVFxY1eW4CBYHMGGvyTN/PORT_4_NEURO.png'
}, {
  id: 5,
  icon: Lightbulb,
  title: 'MEMORY CHAMPIONS',
  subtitle: 'Ages 8-11',
  color: 'bg-[#FF8C00]',
  image: 'https://cdn.magicpatterns.com/uploads/dkE936onor8KvgmMQNHQXq/PORT_5_NEURO.png'
}, {
  id: 6,
  icon: Trophy,
  title: 'YOUNG GENIUSES',
  subtitle: 'Ages 11-12',
  color: 'bg-[#DC143C]',
  image: 'https://cdn.magicpatterns.com/uploads/4xsLgJsoPMxhWVk8rssTxj/PORT_6_NEURO.png'
}];
export function TomosCarousel() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const cardWidth = scrollRef.current.querySelector('div')?.offsetWidth || 280;
    const gap = 24;
    const scrollAmount = cardWidth + gap;
    const newScroll = direction === 'left' ? scrollRef.current.scrollLeft - scrollAmount : scrollRef.current.scrollLeft + scrollAmount;
    scrollRef.current.scrollTo({
      left: newScroll,
      behavior: 'smooth'
    });
  };
  const handleScroll = () => {
    if (!scrollRef.current) return;
    const cardWidth = scrollRef.current.querySelector('div')?.offsetWidth || 280;
    const gap = 24;
    const index = Math.round(scrollRef.current.scrollLeft / (cardWidth + gap));
    setActiveIndex(Math.min(index, includes.length - 1));
  };
  return <section className="bg-white py-16 px-4 overflow-hidden border-b border-border" data-id="element-218">
      <div className="max-w-6xl mx-auto" data-id="element-219">
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} viewport={{
        once: true
      }} className="text-3xl md:text-4xl font-heading font-black text-navy text-center mb-10" data-id="element-220">
          📚 EVERYTHING YOU GET TODAY{' '}
          <span className="text-orange block md:inline mt-2 md:mt-0 text-2xl md:text-3xl" data-id="element-221">
            (6 Tomes + 5 Bonuses)
          </span>
        </motion.h2>

        <div className="relative" data-id="element-222">
          <button onClick={() => scroll('left')} className="hidden md:flex absolute -left-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white border border-border rounded-full items-center justify-center text-navy hover:bg-lightgray transition-colors shadow-md" aria-label="Previous" data-id="element-223">
            <ChevronLeftIcon className="w-6 h-6" data-id="element-224" />
          </button>
          <button onClick={() => scroll('right')} className="hidden md:flex absolute -right-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white border border-border rounded-full items-center justify-center text-navy hover:bg-lightgray transition-colors shadow-md" aria-label="Next" data-id="element-225">
            <ChevronRightIcon className="w-6 h-6" data-id="element-226" />
          </button>

          <div ref={scrollRef} onScroll={handleScroll} className="flex gap-6 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-8 pt-4 px-4 -mx-4 md:px-0 md:mx-0" style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch'
        }} data-id="element-227">
            {includes.map((item, index) => <motion.div key={item.id} initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.4,
            delay: index * 0.08
          }} viewport={{
            once: true
          }} className="flex-shrink-0 snap-center w-[260px] md:w-[300px]" data-id="element-228">
                {item.image ? <div className="rounded-xl border-2 border-navy overflow-hidden shadow-lg transition-transform hover:-translate-y-2 aspect-[3/4] relative bg-lightgray" data-id="element-229">
                    <img src={item.image} alt={`${item.title} — ${item.subtitle}`} loading="lazy" className="w-full h-full object-cover" data-id="element-230" />
                    <span className="absolute top-3 left-3 text-white text-[10px] font-bold tracking-widest uppercase bg-navy/80 px-2 py-1 rounded" data-id="element-231">
                      TOME {index + 1}
                    </span>
                  </div> : <div className={`rounded-xl border-2 border-navy overflow-hidden shadow-lg transition-transform hover:-translate-y-2 aspect-[3/4] flex flex-col relative ${item.color}`} data-id="element-232">
                    <div className="absolute left-0 top-0 bottom-0 w-6 bg-black/20 border-r border-white/10" data-id="element-233"></div>
                    <div className="absolute left-2 top-0 bottom-0 w-px bg-white/20" data-id="element-234"></div>

                    <div className="flex-1 flex flex-col items-center justify-center p-8 text-center relative z-10 ml-4" data-id="element-235">
                      <div className="w-16 h-16 rounded-full border-2 border-white/50 flex items-center justify-center mb-6 bg-black/20" data-id="element-236">
                        <item.icon className="w-8 h-8 text-white" data-id="element-237" />
                      </div>
                      <p className="text-white/90 text-xs font-bold tracking-widest uppercase mb-2 bg-black/30 px-3 py-1 rounded-full" data-id="element-238">
                        TOME {index + 1}
                      </p>
                      <h3 className="text-white text-2xl font-heading font-black leading-tight mb-3 drop-shadow-md" data-id="element-239">
                        {item.title}
                      </h3>
                      <p className="text-white/90 font-bold text-lg bg-black/20 px-4 py-1 rounded-full" data-id="element-240">
                        {item.subtitle}
                      </p>
                    </div>
                  </div>}
              </motion.div>)}
          </div>
        </div>

        <div className="flex justify-center gap-2 mt-2" data-id="element-241">
          {includes.map((_, index) => <button key={index} onClick={() => {
          if (!scrollRef.current) return;
          const cardWidth = scrollRef.current.querySelector('div')?.offsetWidth || 280;
          const gap = 24;
          scrollRef.current.scrollTo({
            left: index * (cardWidth + gap),
            behavior: 'smooth'
          });
        }} className={`h-3 rounded-full transition-all duration-300 ${index === activeIndex ? 'bg-orange w-8' : 'bg-border hover:bg-orange/50 w-3'}`} aria-label={`Go to item ${index + 1}`} data-id="element-242" />)}
        </div>
      </div>
    </section>;
}